import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SingleProfitCalculationApiResponse } from '../../../../ProfitCalculations/types';
import { VictoryArea, VictoryAxis, VictoryChart } from 'victory';
import { colors } from '../../../../../colors';
import {
  calculateLongestTickWidth,
  calculateYAxisWidth,
  calculateYTicks,
  formatYTick,
} from '../../../../../helpers/Chart';
import { ComponentDimensions } from '../../../../../types/Layout';
import useAnimatedChartData from '../../../../../hooks/useAnimatedChartData';

interface Props {
  data: SingleProfitCalculationApiResponse;
}

const SummaryChart = ({ data }: Props) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [parentDimensions, setParentDimensions] = useState<
    ComponentDimensions | undefined
  >();

  useLayoutEffect(() => {
    if (!parentRef.current) {
      return;
    }

    const parentRect = parentRef.current.getBoundingClientRect();
    setParentDimensions({ width: parentRect.width, height: parentRect.height });
  }, []);

  const stableBalanceDatapoints = useMemo(() => {
    if (!Object.keys(data.endBalanceInUsdtHistory)?.length) {
      return [];
    }

    return Object.entries(data.endBalanceInUsdtHistory).map(
      ([timestamp, balance]) => ({
        x: parseInt(timestamp),
        y: balance,
      }),
    );
  }, [data.endBalanceInUsdtHistory]);

  const reducedPriceHistory = useMemo(() => {
    if (!data.priceHistory) {
      return [];
    }

    const datapoints = Object.entries(data.priceHistory).map(
      ([timestamp, balance]) => ({
        x: parseInt(timestamp),
        y: balance,
      }),
    );

    const diff = Math.ceil(datapoints.length / stableBalanceDatapoints.length);
    const filteredDatapoints = datapoints.filter((d, i) => i % diff === 0);

    // Now we must put these dataPoints on the same y axis as the stable balance
    // First datapoints must be set equal to the stable balance first datapoints
    // if the second datapoints raises 0.1%, do this to the new first datapoint to get the second one
    const updatedDatapoints: { x: number; y: number }[] = [];
    const firstStableBalanceDatapoint = stableBalanceDatapoints[0];

    for (let i = 0; i < filteredDatapoints.length; i++) {
      if (i === 0) {
        updatedDatapoints.push(firstStableBalanceDatapoint);
        continue;
      }

      const datapoint = filteredDatapoints[i];
      const previousDatapoint = filteredDatapoints[i - 1].y;
      const change = 1 + (datapoint.y - previousDatapoint) / previousDatapoint;
      const latestNewestValue =
        updatedDatapoints[updatedDatapoints.length - 1].y;
      updatedDatapoints.push({ x: datapoint.x, y: latestNewestValue * change });
    }

    return updatedDatapoints;
  }, [data.priceHistory, stableBalanceDatapoints]);

  const originalDataSets = useMemo(
    () => [stableBalanceDatapoints, reducedPriceHistory],
    [reducedPriceHistory, stableBalanceDatapoints],
  );

  const { animatedDataPoints, domain } = useAnimatedChartData(
    data.baseAsset,
    originalDataSets,
    useMemo(() => [], []),
    true,
    5,
  );
  const animatedStableData = animatedDataPoints[0];
  const animatedPriceData = animatedDataPoints[1];

  const yTicks = calculateYTicks(animatedStableData);
  const longestTickWidth = calculateLongestTickWidth(yTicks, formatYTick);
  const yAxisWidth = calculateYAxisWidth(longestTickWidth);

  const yAxisStyle = {
    axis: { stroke: 'rgba(64,64,64,0.7)' },
    grid: {
      stroke: 'rgba(64,64,64,0.7)',
    },
    ticks: { stroke: 'white', size: 2 },
    tickLabels: {
      fontSize: 16,
      padding: (yAxisWidth - longestTickWidth) / 2,
      fill: colors.white,
    },
  };

  const xAxisStyle = {
    axis: { stroke: 'rgba(64,64,64,0.7)' },
    grid: {
      stroke: 'rgba(64,64,64,0.7)',
    },
    ticks: { stroke: 'white', size: 2 },
    tickLabels: { fontSize: 14, padding: 5, fill: colors.white },
  };

  const isPositiveChart = useMemo(() => {
    if (!animatedStableData?.length) {
      return true;
    }

    return (
      animatedStableData[animatedStableData.length - 1].y >
      animatedStableData[0].y
    );
  }, [animatedStableData]);

  return (
    <div
      ref={parentRef}
      style={{
        flex: 1,
        height: '95%',
        opacity: parentDimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
      }}
    >
      {parentDimensions && (
        <VictoryChart
          domain={domain}
          scale={{ x: 'time', y: 'linear' }}
          width={parentDimensions.width}
          height={parentDimensions.height}
          padding={{ left: yAxisWidth, right: 0, top: 15, bottom: 50 }}
        >
          <VictoryAxis style={xAxisStyle} tickCount={8} fixLabelOverlap />
          <VictoryAxis
            style={yAxisStyle}
            dependentAxis
            tickFormat={formatYTick}
            width={yAxisWidth}
            tickCount={8}
          />
          <VictoryArea
            data={animatedStableData}
            style={{
              data: {
                fill: `url(#${
                  isPositiveChart
                    ? 'positiveStableColor'
                    : 'negativeStableColor'
                })`,
                stroke: isPositiveChart ? colors.mossGreen : colors.red,
                strokeWidth: 1,
              },
            }}
          />
          <VictoryArea
            data={animatedPriceData}
            style={{
              data: {
                fill: 'url(#priceColor)',
                stroke: colors.white,
                strokeWidth: 1,
              },
            }}
          />
        </VictoryChart>
      )}
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="priceColor" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.white} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.white} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient
            id="positiveStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.mossGreen} stopOpacity={0.3} />
            <stop
              offset="100%"
              stopColor={colors.mossGreen}
              stopOpacity={0.04}
            />
          </linearGradient>
          <linearGradient
            id="negativeStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default SummaryChart;
